import React, { ChangeEvent } from 'react';

import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { Editor } from '.';

import { Input, Button, FormGroup, Checkbox } from '@spark-labs/avocado-ui';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';
import { BubbleInput, BubbleTextArea } from './BubbleUI';
import { toast } from './UIkit/Toast/Toast';

export interface ContentTemplateFormProps {
  contentTemplate: BareContentTemplate;
  mode: 'edit' | 'create';
  onSubmit?: (contentTemplate: BareContentTemplate) => void;
  onArchive?: () => void;
  showNewContentBtn: boolean;
  showDailyProgramOptions: boolean;
}

export interface ContentTemplateFormState {
  editedContentTemplate: BareContentTemplate;
  isAdvanced: boolean;
}

export default class ContentTemplateForm extends React.Component<
  ContentTemplateFormProps,
  ContentTemplateFormState
> {
  public readonly state: ContentTemplateFormState = {
    editedContentTemplate: this.props.contentTemplate,
    isAdvanced: false,
  };

  constructor(props: ContentTemplateFormProps) {
    super(props);

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleHideFromDailyProgramChange = this.handleHideFromDailyProgramChange.bind(this);
  }

  public render() {
    let contentTemplate = this.state.editedContentTemplate;

    return (
      <div className="border-t border-grey pt-2 p-4 mt-6" style={{ width: '720px' }}>
        <fieldset disabled={this.isLocked()}>
          <div className="flex">
            <div className="w-full p-4">
              <FormGroup className="flex items-end">
                <div className="flex-grow mr-3">
                  <Input
                    name="name"
                    label="Name"
                    value={contentTemplate.name}
                    onChange={this.handleInputChange}
                  />
                </div>
                <div>
                  <span>Show HTML</span>
                  <BubbleInput
                    checked={this.state.isAdvanced}
                    onValue={isAdvanced => this.setState({ isAdvanced })}
                    type="checkbox"
                    className="ml-1"
                    inputClassName="shadow-none-i"
                  />
                </div>
              </FormGroup>
              <FormGroup>
                {this.state.isAdvanced ? (
                  <BubbleTextArea
                    block
                    className="flex-grow"
                    value={this.state.editedContentTemplate.content}
                    onValue={this.handleEditorChange}
                  />
                ) : (
                  <Editor
                    value={this.state.editedContentTemplate.content}
                    onValue={this.handleEditorChange}
                  />
                )}
              </FormGroup>
              {this.props.showDailyProgramOptions && (
                <FormGroup>
                  <Checkbox
                    onChange={this.handleHideFromDailyProgramChange}
                    label="Hide from Digital Endpoints"
                    checked={!!this.state.editedContentTemplate.hideFromDigitalDailyProgram}
                  />
                </FormGroup>
              )}
            </div>
          </div>
          {!this.isLocked() && (
            <div className="flex justify-end">
              {this.props.mode === 'edit' && (
                <div className="mx-2">
                  <Button
                    color="red"
                    onClick={this.props.onArchive ? this.props.onArchive : undefined}
                  >
                    {this.state.editedContentTemplate.archived ? 'Unarchive' : 'Archive'}
                  </Button>
                </div>
              )}
              <div className="mx-2">
                <Button color="green" onClick={this.handleFormSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      </div>
    );
  }

  private isLocked = () => !this.props.showNewContentBtn;

  private async handleFormSubmit() {
    if (this.isLocked()) {
      await toast.warning('Content template editing is locked');
      return;
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(this.state.editedContentTemplate);
    }
  }

  private handleInputChange(event: ChangeEvent<InputElement>) {
    let newState = { ...this.state.editedContentTemplate, [event.target.name]: event.target.value };
    this.setState({ editedContentTemplate: newState as any });
  }

  private handleEditorChange(content: string) {
    this.setState({ editedContentTemplate: { ...this.state.editedContentTemplate, content } });
  }

  private handleHideFromDailyProgramChange(event: ChangeEvent<HTMLInputElement>) {
    this.setState({
      editedContentTemplate: {
        ...this.state.editedContentTemplate,
        hideFromDigitalDailyProgram: event.target.checked,
      },
    });
  }
}
